import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Lock, ArrowLeft, Pencil, Trash2, FileBarChart, Sparkles, Archive, ArchiveRestore, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { usePhone } from "@/hooks/useMedia";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFetch, useUsers } from "@/lib/hooks";
import { FullLoader } from "@/components/core/AppLayout";
import { ClientFormDialog } from "@/components/core/ClientFormDialog";
import { useAuth } from "@/context/AuthContext";
import { ClientDataPanel, ClientDeleteHost } from "@/components/client/ClientDataPanel";
import { ClientStatoBadge } from "@/components/core/TaskBadges";
import { ClientPed } from "@/components/client/ClientPed";
import { ClientAnagrafica } from "@/components/client/ClientAnagrafica";
import { ClientVisibility, ReservedBadge } from "@/components/client/ClientVisibility";
import { ClientAvatar } from "@/components/client/ClientAvatar";
import { ClientBranding } from "@/components/client/ClientBranding";
import { ClientDocuments } from "@/components/client/ClientDocuments";
import { ClientTasks } from "@/components/client/ClientTasks";
import { ClientAccessi } from "@/components/client/ClientAccessi";
import { ClientCalendar } from "@/components/client/ClientCalendar";
import { ClientPortal } from "@/components/client/ClientPortal";
import { ReportDialog } from "@/components/client/ReportDialog";
import { ClientCampaigns } from "@/components/client/ClientCampaigns";
import { ClientEmails } from "@/components/client/ClientEmails";
import { ClientNotes } from "@/components/client/ClientNotes";
import { InvoicesWorkspace } from "@/components/invoices/InvoicesWorkspace";
import { AiSummaryDialog } from "@/components/ai/AiSummaryDialog";
import { ReservedLine } from "@/components/client/ReservedLine";
import { AiEmailButton } from "@/components/ai/AiEmailDialog";
import { ClientVideoScripts } from "@/components/adminai/ClientVideoScripts";
import { ClientCompetitor } from "@/components/adminai/ClientCompetitor";

const TABS = [["anagrafica", "Anagrafica"], ["documenti", "Documenti"], ["task", "Task"], ["accessi", "Accessi"], ["calendario", "Calendario"], ["ped", "PED"], ["campagne", "Campagne"], ["email", "Email"], ["portale", "Portale cliente"], ["fatture", "Fatture"], ["appunti", "I miei appunti"]];
const ADMIN_TABS = [["script", "Script video", true], ["competitor", "Competitor & trend", true]];

const toggleArchive = async (client, onChanged) => {
  const archived = client.stato === "archiviato";
  try {
    const r = await api.patch(`/clients/${client.id}/archivio`, { archiviato: !archived });
    onChanged(r.data); toast.success(archived ? "Cliente ripristinato dall'archivio" : "Cliente archiviato");
  } catch (e) { toast.error(errMsg(e)); }
};

const MobileActions = ({ client, isAdmin, onReport, onEdit, onDelete, onChanged }) => {
  const archived = client.stato === "archiviato";
  const item = "min-h-[44px] font-semibold";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="rounded-full h-11 w-11 p-0 border-[#0A1128]/20" aria-label="Altre azioni" data-testid="client-actions-menu"><MoreHorizontal className="h-5 w-5" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem className={item} onSelect={onReport} data-testid="client-menu-report"><FileBarChart className="h-4 w-4 mr-2" />Report</DropdownMenuItem>
        <DropdownMenuItem className={item} onSelect={() => toggleArchive(client, onChanged)} data-testid="client-menu-archive">{archived ? <><ArchiveRestore className="h-4 w-4 mr-2" />Ripristina</> : <><Archive className="h-4 w-4 mr-2" />Archivia</>}</DropdownMenuItem>
        <DropdownMenuItem className={item} onSelect={onEdit} data-testid="client-menu-edit"><Pencil className="h-4 w-4 mr-2" />Modifica</DropdownMenuItem>
        {isAdmin && <><DropdownMenuSeparator /><DropdownMenuItem className={`${item} text-[#E8173A]`} onSelect={onDelete} data-testid="client-menu-delete"><Trash2 className="h-4 w-4 mr-2" />Elimina</DropdownMenuItem></>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ArchiveButton = ({ client, onChanged }) => {
  const archived = client.stato === "archiviato";
  const toggle = () => toggleArchive(client, onChanged);
  return (
    <Button variant="outline" onClick={toggle} className="rounded-full border-[#0A1128]/20 text-[#0A1128] font-bold" data-testid="client-archive-toggle">
      {archived ? <><ArchiveRestore className="h-4 w-4 mr-1.5" />Ripristina</> : <><Archive className="h-4 w-4 mr-1.5" />Archivia</>}
    </Button>
  );
};

export default function ClientDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { data: client, setData } = useFetch(`/clients/${id}`);
  const { data: users } = useUsers();
  const [edit, setEdit] = useState(false);
  const [del, setDel] = useState(false);
  const [report, setReport] = useState(false);
  const [summary, setSummary] = useState(false);
  const [sp, setSp] = useSearchParams();
  const phone = usePhone();
  const tab = sp.get("tab") || "anagrafica";
  if (!client) return <FullLoader />;

  return (
    <div data-testid="client-detail-page">
      <Link to="/clienti" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-[#E8173A] mb-5" data-testid="client-back-link"><ArrowLeft className="h-4 w-4" />Tutti i clienti</Link>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6 lg:mb-8 core-rise">
        <div className="flex items-center gap-4 sm:gap-5 min-w-0">
          <div className="relative shadow-lg rounded-2xl">
            <ClientAvatar client={client} size="lg" dark testid="client-detail-avatar" />
            <span className="absolute -right-1 -top-1 h-4 w-4 rounded-full bg-[#E8173A] border-2 border-white" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl break-words font-black tracking-tight text-[#0A1128]" data-testid="client-detail-name">{client.ragione_sociale}</h1>
              {client.visibilita === "riservato" && <ReservedBadge testid="client-detail-reserved" />}
            </div>
            <div>
              <ReservedLine clientId={client.id} testid="client-detail-reserved-line" />
              <ClientStatoBadge stato={client.stato} />
            </div>
            <p className="text-sm text-slate-500 mt-1">{[client.settore, client.socio_nome && `Socio: ${client.socio_nome}`].filter(Boolean).join(" · ") || "—"}</p>
          </div>
        </div>
        {phone ? (
          <div className="flex flex-wrap items-center gap-2" data-testid="client-mobile-actions">
            <Button onClick={() => setSummary(true)} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold h-11" data-testid="client-ai-summary-button"><Sparkles className="h-4 w-4 mr-1.5" />Riassunto AI</Button>
            <AiEmailButton client={client} />
            <MobileActions client={client} isAdmin={isAdmin} onReport={() => setReport(true)} onEdit={() => setEdit(true)} onDelete={() => setDel(true)} onChanged={setData} />
          </div>
        ) : (
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => setSummary(true)} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="client-ai-summary-button"><Sparkles className="h-4 w-4 mr-1.5" />Riassunto AI</Button>
          <AiEmailButton client={client} />
          <Button onClick={() => setReport(true)} className="rounded-full bg-[#0A1128] hover:bg-[#E8173A] font-bold" data-testid="client-report-button"><FileBarChart className="h-4 w-4 mr-1.5" />Report</Button>
          <ArchiveButton client={client} onChanged={setData} />
          <Button variant="outline" onClick={() => setEdit(true)} className="rounded-full border-[#0A1128]/20 text-[#0A1128] font-bold" data-testid="client-detail-edit"><Pencil className="h-4 w-4 mr-1.5" />Modifica</Button>
          {isAdmin && <Button variant="outline" onClick={() => setDel(true)} className="rounded-full border-red-200 text-[#E8173A] hover:bg-red-50 hover:text-[#E8173A] font-bold" data-testid="client-detail-delete"><Trash2 className="h-4 w-4 mr-1.5" />Elimina</Button>}
        </div>
        )}
      </div>

      {client.stato === "archiviato" && (
        <div className="mb-6 rounded-2xl border-2 border-amber-300 bg-amber-50 px-5 py-3 text-sm text-amber-900 flex items-center gap-2" data-testid="client-archived-banner">
          <Archive className="h-4 w-4" />Cliente archiviato: è nascosto da elenchi, calendari, bacheca, dashboard ed email automatiche, e il suo portale non è disponibile. I dati restano conservati.
        </div>
      )}
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v }, { replace: true })} className="core-rise core-delay-1">
        <TabsList className="bg-[#A6DAFC] border border-slate-200 rounded-full p-1 h-auto mb-6 max-w-full" data-testid="client-tabs">
          {[...TABS, ...(isAdmin ? ADMIN_TABS : [])].map(([k, l, lock]) => (
            <TabsTrigger key={k} value={k} className="rounded-full px-5 py-2 font-bold text-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid={`client-tab-${k}`}>{lock && <Lock className="h-3.5 w-3.5 mr-1 text-[#E8173A]" />}{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="anagrafica"><ClientAnagrafica client={client} /><ClientVisibility client={client} onChanged={setData} /><ClientBranding client={client} onChanged={setData} /><ClientDataPanel client={client} /></TabsContent>
        <TabsContent value="documenti"><ClientDocuments clientId={id} /></TabsContent>
        <TabsContent value="task"><ClientTasks clientId={id} users={users || []} /></TabsContent>
        <TabsContent value="accessi"><ClientAccessi clientId={id} /></TabsContent>
        <TabsContent value="calendario"><ClientCalendar clientId={id} users={users || []} /></TabsContent>
        <TabsContent value="ped"><ClientPed client={client} users={users || []} /></TabsContent>
        <TabsContent value="campagne"><ClientCampaigns client={client} /></TabsContent>
        <TabsContent value="email"><ClientEmails client={client} /></TabsContent>
        <TabsContent value="portale"><ClientPortal client={client} /></TabsContent>
        <TabsContent value="fatture"><InvoicesWorkspace clientId={client.id} /></TabsContent>
        <TabsContent value="appunti"><ClientNotes client={client} /></TabsContent>
        {isAdmin && <TabsContent value="script"><ClientVideoScripts client={client} /></TabsContent>}
        {isAdmin && <TabsContent value="competitor"><ClientCompetitor client={client} /></TabsContent>}
      </Tabs>

      <AiSummaryDialog open={summary} onOpenChange={setSummary} client={client} />
      <ReportDialog open={report} onOpenChange={setReport} client={client} />
      <ClientFormDialog open={edit} onOpenChange={setEdit} client={client} users={users || []} onSaved={setData} />
      <ClientDeleteHost client={client} trigger={del} onClose={() => setDel(false)} />
    </div>
  );
}
